import { useEffect, useRef } from "react";

/**
 * A subtle gold hairline fixed to the top of the viewport that fills
 * left-to-right as the user scrolls — reinforces the editorial rhythm
 * and sits just above the sticky Navbar.
 *
 * Implemented with a single passive scroll listener + a CSS transform
 * so the App shell does NOT pull in framer-motion on the initial bundle
 * (that library was the largest mobile TBT/bundle offender).
 */
export function ScrollProgress() {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ticking = false;
    const update = () => {
      const el = barRef.current;
      if (!el) {
        ticking = false;
        return;
      }
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
      el.style.transform = `scaleX(${progress})`;
      ticking = false;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div
      ref={barRef}
      aria-hidden="true"
      style={{ transform: "scaleX(0)", transformOrigin: "left" }}
      className="fixed top-0 left-0 right-0 z-[60] h-[2px] bg-gradient-to-r from-accent/30 via-accent to-accent/30 pointer-events-none will-change-transform"
    />
  );
}
